<template>
  <div class="shift-info-bar">
    <div class="shift-info-lead">
      <div class="info-pill info-pill--shift">
        <span class="info-pill-label">{{ $t('shifts') }}</span>
        <strong class="info-pill-value">{{ displayShiftNames }}</strong>
      </div>
      <div class="info-pill info-pill--date">
        <span class="info-pill-label">{{ $t('date') }}</span>
        <span class="info-pill-value">{{ displayDate }}</span>
      </div>
      <template v-if="showDayCounts">
        <div class="info-pill info-pill--count">
          <span class="info-pill-label">{{ $t('complaints') }}</span>
          <strong class="info-pill-value">{{ displayComplaints }}</strong>
        </div>
        <div class="info-pill info-pill--count">
          <span class="info-pill-label">{{ $t('call_cleaner') }}</span>
          <strong class="info-pill-value">{{ displayCallCleaners }}</strong>
        </div>
      </template>
    </div>
    <div class="shift-info-times" role="group" :aria-label="timesGroupAriaLabel">
      <div class="time-chip">
        <span class="time-chip-label">{{ $t('site') }}</span>
        <span class="time-chip-value" aria-live="polite">{{ clock }}</span>
      </div>
      <div class="time-chip">
        <span class="time-chip-label">{{ $t('local_time') }}</span>
        <span class="time-chip-value" aria-live="polite">{{ localClock }}</span>
      </div>
    </div>
    <div v-if="isInactive" class="inactive-banner" role="status">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
      </svg>
      <span>{{ $t('shift_currently_inactive') }}</span>
    </div>
  </div>
</template>

<script>
import utilService from '../../common/services/utilService'

export default {
  props: {
    /**
     * @typedef {import('../../control-panel/services/shiftService').Shift} Shift
     * @type {{new():Shift}
     */
    currShift: { type: Object, required: true },
    /** Day totals since company-local midnight (site-wide). */
    dayCounts: {
      type: Object,
      default: () => ({ complaints: null, callCleaners: null }),
    },
    /** Day counts are cleaning totals, shown only to users who may see the cleaning tab. */
    showDayCounts: { type: Boolean, default: true },
  },
  data() {
    return {
      clock: null,
      localClock: null,
      tick: 0,
    };
  },
  created() {
    this.clock = this.currTime();
    this.localClock = this.currLocalTime();
    this.clockInterval = setInterval(() => {
      this.tick = Date.now();
      this.clock = this.currTime();
      this.localClock = this.currLocalTime();
    }, 1000 * 30);
  },
  beforeDestroy() {
    if (this.clockInterval) clearInterval(this.clockInterval);
  },
  methods: {
    shiftGmt() {
      return this.currShift && this.currShift.gmt != null && this.currShift.gmt !== '' ? this.currShift.gmt : null
    },
    currTime() {
      return utilService.formatHMInCompanyTz(new Date(), this.shiftGmt())
    },
    currLocalTime() {
      const now = new Date();
      return now.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  computed: {
    timesGroupAriaLabel() {
      return `${this.$t('site')} — ${this.$t('local_time')}`;
    },
    /** Current date in company timezone only. No local time fallback. */
    displayDate() {
      this.tick;
      const now = new Date();
      const tz = utilService.timeZoneForGmt(this.shiftGmt());
      if (tz) {
        const day = now.toLocaleString('en-GB', { timeZone: tz, day: 'numeric' });
        const month = now.toLocaleString('en-GB', { timeZone: tz, month: 'numeric' });
        const year = now.toLocaleString('en-GB', { timeZone: tz, year: 'numeric' });
        const monthNum = parseInt(month, 10);
        return `${day} ${this.$t('monthMap[' + monthNum + ']')} ${year}`;
      }
      return '--';
    },
    isInactive() {
      const shifts = this.concurrentShifts
      return shifts.some((shift) => shift && shift.is_active === 0)
    },
    concurrentShifts() {
      if (Array.isArray(this.currShift.currentShifts) && this.currShift.currentShifts.length > 0) {
        return this.currShift.currentShifts
      }
      return [this.currShift]
    },
    displayShiftNames() {
      const names = this.concurrentShifts
        .map((shift) => shift.shift_name || shift.shiftName)
        .filter(Boolean)
      return names.length > 0 ? names.join(', ') : '--'
    },
    displayComplaints() {
      const value = this.dayCounts && this.dayCounts.complaints
      return value == null || Number.isNaN(Number(value)) ? '--' : Number(value)
    },
    displayCallCleaners() {
      const value = this.dayCounts && this.dayCounts.callCleaners
      return value == null || Number.isNaN(Number(value)) ? '--' : Number(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.shift-info-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  min-width: 0;
}

.shift-info-lead {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 6px;
  min-width: 0;
}

.info-pill {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  padding: 4px 10px 5px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.035);
  min-height: 0;
  min-width: 0;
}

.info-pill-label {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
  line-height: 1.15;
}

.info-pill-value {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-pill--shift .info-pill-value {
  color: #312e81;
  max-width: 360px;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.info-pill--date .info-pill-value {
  font-weight: 600;
  color: #334155;
  max-width: 240px;
}

.info-pill--count .info-pill-value {
  font-variant-numeric: tabular-nums;
  color: #0f172a;
}

.shift-info-times {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 5px;
}

.time-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 58px;
  padding: 4px 8px 5px;
  border-radius: 6px;
  background: linear-gradient(180deg, #fff 0%, #f1f5f9 100%);
  border: 1px solid #cbd5e1;
}

.time-chip-label {
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.time-chip-value {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  letter-spacing: 0.02em;
}

.inactive-banner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 100%;
  padding: 5px 10px;
  border-radius: 6px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: #d97706;
  }
}

@media (max-width: 768px) {
  .shift-info-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .shift-info-lead {
    width: 100%;
  }

  .shift-info-times {
    width: 100%;
    justify-content: space-between;
  }

  .info-pill-value,
  .info-pill--date .info-pill-value {
    max-width: none;
  }

  .inactive-banner {
    width: 100%;
  }
}

@media (max-width: 520px) {
  .info-pill-value {
    max-width: 140px;
  }
  .info-pill--date .info-pill-value {
    max-width: 170px;
  }
}
</style>
